// Direction B "Ink Header" action card — shared primitive for the 3
// moment-of-truth cards (Booking / Reschedule / Cancellation).
// Per design handoff:
//   - Warm-ink (#1a1614) bg, white text, 1px (#2a2421) border, 14px radius.
//   - Eyebrow: mono uppercase 10px / 0.18em / inline icon. 3 status colors:
//     'success' (green check), 'muted' (white text), 'danger' (brand-pink + cancel icon).
//   - Title: 15px / 600 / -0.01em / white.
//   - Hairline rule: 1px rgba(255,255,255,0.08), full-bleed across the card.
//   - Rows: each row = mono uppercase LABEL (38px min-width) + value text.
//   - Foot: 12px muted-500 line below the rows; bold spans render as white.
// Card sits inside the assistant message row's right column (after the 28px
// avatar slot), max-width 280, animates in with the bubble keyframe.

import { CHECK_SVG, CANCEL_SVG } from '../../icons/icons'

export type ActionCardStatus = 'success' | 'muted' | 'danger'

export interface ActionCardRow {
  label: string
  text: React.ReactNode
}

export interface ActionCardProps {
  status: ActionCardStatus
  eyebrow: string
  title: string
  rows: ActionCardRow[]
  foot?: React.ReactNode
  // testid passed through so existing tests can still locate the card.
  testId?: string
}

export function ActionCard({ status, eyebrow, title, rows, foot, testId }: ActionCardProps) {
  return (
    <div
      data-testid={testId}
      className={[
        'w-full max-w-[280px] p-4 rounded-2xl flex flex-col gap-2.5',
        'bg-warmink text-white border border-warmink-border',
        'animate-bubbleIn',
      ].join(' ')}
    >
      <Eyebrow status={status}>{eyebrow}</Eyebrow>
      <div className="text-[15px] font-semibold tracking-[-0.01em] leading-[1.25] text-white">
        {title}
      </div>
      {/* Hairline rule — full-bleed across the 16px card padding */}
      <div className="h-px bg-white/[0.08] -mx-4" />
      <div className="flex flex-col gap-[7px] text-[13px] text-white leading-[1.4]">
        {rows.map((r, i) => (
          <div key={i} className="flex items-start gap-2">
            <span className="font-mono text-[9.5px] uppercase tracking-eyebrow text-muted-500 min-w-[38px] flex-shrink-0 pt-[3px]">
              {r.label}
            </span>
            <span className="flex-1">{r.text}</span>
          </div>
        ))}
      </div>
      {foot && (
        <div className="text-[12px] text-muted-500 leading-[1.4] mt-0.5 [&>b]:text-white [&>b]:font-medium">
          {foot}
        </div>
      )}
    </div>
  )
}

interface EyebrowProps {
  status: ActionCardStatus
  children: React.ReactNode
}

function Eyebrow({ status, children }: EyebrowProps) {
  const cls =
    status === 'success' ? 'text-success' : status === 'danger' ? 'text-accent' : 'text-muted-500'
  const icon = status === 'danger' ? CANCEL_SVG : CHECK_SVG
  return (
    <div
      className={`font-mono text-[10px] font-medium uppercase tracking-eyebrow-wide inline-flex items-center gap-1.5 ${cls}`}
    >
      <span dangerouslySetInnerHTML={{ __html: icon }} className="w-[11px] h-[11px] inline-block" />
      {children}
    </div>
  )
}
